import { useTranslation } from 'react-i18next';
import { Header } from "@/components/Header";
import { SubscribeCard } from "@/components/SubscribeCard";
import { EventsSection, PastEventsSection } from "@/components/EventsSection";
import { Changelog } from "@/components/Changelog";
import { AboutCard } from "@/components/AboutCard";
import { Footer } from "@/components/Footer";
import { useHomeData } from "@/hooks/useHomeData";
import { tryParseDateOnly } from "@/lib/date";

function parseToday(todayDate: string, todayDateIso?: string): Date | null {
  const parsed = tryParseDateOnly(todayDateIso ?? todayDate);
  if (parsed) {
    return parsed;
  }
  const legacy = /^(\d{4})年(\d{2})月(\d{2})日/.exec(todayDate);
  if (!legacy) {
    return null;
  }
  return tryParseDateOnly(`${legacy[1]}-${legacy[2]}-${legacy[3]}`);
}

/* Front-page header: the year and weekday as a kicker, the date as the serif title. */
function DateHeader({ todayDate, todayDateIso }: { todayDate: string; todayDateIso?: string }) {
  const { t, i18n } = useTranslation('public');
  const today = parseToday(todayDate, todayDateIso);
  let kicker = '';
  let title = todayDate;
  if (today) {
    const language = i18n.resolvedLanguage;
    const year = new Intl.DateTimeFormat(language, { year: 'numeric' }).format(today);
    const weekday = new Intl.DateTimeFormat(language, { weekday: 'long' }).format(today);
    kicker = `${year} · ${weekday}`;
    title = new Intl.DateTimeFormat(language, { month: 'long', day: 'numeric' }).format(today);
  }
  return (
    <header className="flex flex-col gap-2.5 border-b border-rule pb-6 pt-8 md:pb-8 md:pt-10">
      {kicker && <span className="kicker">{kicker}</span>}
      <h1 className="m-0 text-[2rem] leading-none md:text-[3rem]">{title}</h1>
      <p className="m-0 max-w-[60ch] text-base leading-relaxed text-ink-2">{t('about.description')}</p>
    </header>
  );
}

// 错误重试组件
function ErrorRetry({ onRetry }: { onRetry?: () => void }) {
  const { t } = useTranslation('public');
  return (
    <div className="py-16">
      <p className="m-0 text-sm text-ink-2">{t('loadError')}</p>
      <button
        type="button"
        onClick={onRetry || (() => window.location.reload())}
        className="text-link mt-4"
      >
        {t('reload')}
      </button>
    </div>
  );
}

export function HomePage() {
  const { t } = useTranslation('public');
  const { data, loading, error } = useHomeData();

  let content = <p className="kicker py-16">{t('loading')}</p>;
  if (!loading && (error || !data)) {
    content = <ErrorRetry />;
  } else if (data) {
    content = (
      <>
        <DateHeader todayDate={data.today_date} todayDateIso={data.today_date_iso} />
        <div className="grid grid-cols-1 gap-x-6 gap-y-10 pt-8 md:grid-cols-12 md:pt-10">
          {/* Main column: today's events and the recent past. */}
          <div className="flex flex-col gap-10 md:col-span-8">
            <EventsSection events={data.events} isToday={data.is_today} />
            <PastEventsSection events={data.past_events} />
          </div>

          {/* Side column: subscription, updates and the colophon. */}
          <aside className="flex flex-col gap-10 md:col-span-4">
            <SubscribeCard />
            <Changelog logs={data.change_log} />
            <AboutCard />
          </aside>
        </div>
      </>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="global-container flex-1" aria-busy={loading}>
        {content}
      </main>
      <Footer />
    </div>
  );
}
